import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Card,
  CardBody,
  Skeleton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Alert,
  AlertIcon,
  Button,
  HStack,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Input,
} from '@chakra-ui/react'
import { Upload } from 'lucide-react'
import ReportPageHeader from '@/components/reports/ReportPageHeader'
import { helpdeskReportService, type ReportFilters } from '@/lib/services/helpdeskReportService'
import { useToastNotification } from '@/lib/utils/useToastNotification'

type PivotRow = {
  period_month: string
  agent: string
  channel: string
  handled: number
  aht_seconds: number | null
  dialed: number
  answered: number
  answer_rate: number | null
  email_count: number
  status_ready: number
  status_unavailable: number
  status_lunch: number
  status_logoff: number
}

export default function ContactCenterProductivityPage() {
  const [filters, setFilters] = useState<ReportFilters>({})
  const [rows, setRows] = useState<PivotRow[]>([])
  const [counts, setCounts] = useState({ fact_rows: 0, agents: 0, batches: 0 })
  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess } = useToastNotification()

  const load = () => {
    setLoading(true)
    helpdeskReportService
      .getContactCenterProductivity(filters)
      .then((res) => {
        setRows((res.monthly_pivot as PivotRow[]) ?? [])
        setCounts({
          fact_rows: res.counts?.fact_rows ?? 0,
          agents: res.counts?.agents ?? 0,
          batches: res.counts?.batches ?? 0,
        })
        setNote(res.availability?.notes ?? '')
      })
      .catch(() => {
        setRows([])
        setCounts({ fact_rows: 0, agents: 0, batches: 0 })
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.from, filters.to, filters.client_id])

  const onFile = async (file: File | null) => {
    if (!file) return
    setUploading(true)
    try {
      const result = await helpdeskReportService.importContactCenterProductivity(file, filters)
      showSuccess(
        'Import complete',
        `Inserted ${result.inserted} row(s)${result.skipped ? `, skipped ${result.skipped}` : ''}.`
      )
      load()
    } catch (err) {
      showError(err)
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <Box p={4}>
      <ReportPageHeader
        title="Contact Center Productivity"
        subtitle="Inbound / outbound / email / status pivot from imported WFM or telephony CSV (Claude Pivot path)"
        reportType="contact-center-productivity"
        filters={filters}
        onFiltersChange={setFilters}
      />

      <Alert status="info" mb={4} borderRadius="md">
        <AlertIcon />
        <Text fontSize="sm">
          {note ||
            'Upload a CSV with Agent, Month, Channel, Handled, AHT, ring/talk/hold/wrap, dialed/answered, or status counts.'}
        </Text>
      </Alert>

      <HStack mb={4} spacing={2} flexWrap="wrap">
        <Input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          display="none"
          onChange={(e) => onFile(e.target.files?.[0] ?? null)}
        />
        <Button
          size="sm"
          colorScheme="blue"
          leftIcon={<Upload size={14} />}
          isLoading={uploading}
          onClick={() => fileRef.current?.click()}
        >
          Import CSV
        </Button>
        <Text fontSize="xs" color="gray.500">
          Template columns are included in the Excel download.
        </Text>
      </HStack>

      <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3} mb={4}>
        {(
          [
            ['Fact rows', counts.fact_rows],
            ['Agents', counts.agents],
            ['Import batches', counts.batches],
          ] as const
        ).map(([label, value]) => (
          <Card key={label}>
            <CardBody>
              <Stat>
                <StatLabel fontSize="xs">{label}</StatLabel>
                {loading ? <Skeleton height="8" mt={2} /> : <StatNumber fontSize="xl">{value}</StatNumber>}
              </Stat>
            </CardBody>
          </Card>
        ))}
      </SimpleGrid>

      <Card>
        <CardBody overflowX="auto">
          {loading ? (
            <Skeleton height="240px" />
          ) : (
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Month</Th>
                  <Th>Agent</Th>
                  <Th>Channel</Th>
                  <Th isNumeric>Handled</Th>
                  <Th isNumeric>AHT (s)</Th>
                  <Th isNumeric>Dialed</Th>
                  <Th isNumeric>Answered</Th>
                  <Th isNumeric>Answer %</Th>
                  <Th isNumeric>Email</Th>
                  <Th isNumeric>Ready</Th>
                  <Th isNumeric>Unavail</Th>
                  <Th isNumeric>Lunch</Th>
                  <Th isNumeric>Logoff</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.length === 0 ? (
                  <Tr>
                    <Td colSpan={13}>
                      <Text fontSize="sm" color="gray.500">
                        No imported telephony/WFM facts yet. Import a CSV to populate the monthly pivot.
                      </Text>
                    </Td>
                  </Tr>
                ) : (
                  rows.map((r) => (
                    <Tr key={`${r.period_month}-${r.agent}-${r.channel}`}>
                      <Td whiteSpace="nowrap">{r.period_month}</Td>
                      <Td>{r.agent}</Td>
                      <Td>{r.channel}</Td>
                      <Td isNumeric>{r.handled}</Td>
                      <Td isNumeric>{r.aht_seconds ?? '—'}</Td>
                      <Td isNumeric>{r.dialed}</Td>
                      <Td isNumeric>{r.answered}</Td>
                      <Td isNumeric>
                        {r.answer_rate != null ? `${(Number(r.answer_rate) * 100).toFixed(1)}%` : '—'}
                      </Td>
                      <Td isNumeric>{r.email_count}</Td>
                      <Td isNumeric>{r.status_ready}</Td>
                      <Td isNumeric>{r.status_unavailable}</Td>
                      <Td isNumeric>{r.status_lunch}</Td>
                      <Td isNumeric>{r.status_logoff}</Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </Box>
  )
}
